import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { ShieldCheck, Leaf, Thermometer, Award } from "lucide-react";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";

/**
 * "Facts behind the harvest" — producer's record section on landing.
 * Structured around verifiable metrics, product facts and the producer's own
 * sensory profile. No testimonials, no external awards, no invented language.
 * Copy lives in ecommerce.js → proof.*.
 */
export default function SocialProof() {
  const { t } = useLang();
  const metrics = t("proof.metrics") || [];
  const credentials = t("proof.credentials") || [];
  const panel = t("proof.panel_attrs") || [];

  return (
    <section
      id="hechos"
      data-testid="social-proof"
      className="bg-olive-900 text-cream-100 py-24 sm:py-32 border-t border-olive-800"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.32em] text-gold-deep mb-5">
            {t("proof.overline")}
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-cream-100">
            {t("proof.title_a")}{" "}
            <span className="italic text-gold">{t("proof.title_b")}</span>{" "}
            {t("proof.title_c")}.
          </h2>
          <p className="mt-6 text-cream-100/70 text-base sm:text-lg leading-relaxed max-w-2xl font-light">
            {t("proof.body")}
          </p>
        </div>

        {/* Verifiable metrics */}
        {PRE_HARVEST_MODE && (
          <p
            data-testid="social-proof-reference-note"
            className="mt-8 text-xs text-cream-100/50 border-l-2 border-gold-deep/50 pl-3 italic max-w-2xl"
          >
            {t("pre_harvest.reference_note")}
          </p>
        )}
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-olive-800 border border-olive-800">
          {Array.isArray(metrics) &&
            metrics.map((m, i) => (
              <div
                key={m.v || m.k}
                data-testid={`proof-metric-${i}`}
                className="bg-olive-900 p-7 lg:p-9"
              >
                <div className="font-serif text-4xl lg:text-5xl text-gold leading-none">
                  {m.k}
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-cream-100/80">
                  {m.v}
                </div>
                <p className="mt-3 text-cream-100/60 text-sm leading-relaxed font-light">
                  {m.sub}
                </p>
              </div>
            ))}
        </div>

        <div className="mt-20 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Product details list */}
          <div className="lg:col-span-5">
            <h3 className="font-serif text-2xl text-cream-100 mb-6 flex items-center gap-3">
              <ShieldCheck size={22} strokeWidth={1.2} className="text-gold" />
              {t("proof.credentials_title")}
            </h3>
            <ul className="space-y-4">
              {credentials.map((c, i) => (
                <li
                  key={c}
                  data-testid={`proof-credential-${i}`}
                  className="flex items-start gap-3 text-cream-100/80 text-[15px] font-light leading-relaxed border-b border-olive-800 pb-4"
                >
                  <Leaf
                    size={15}
                    strokeWidth={1.2}
                    className="text-gold-deep mt-1.5 shrink-0"
                  />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Producer's sensory profile */}
          <div className="lg:col-span-7">
            <h3 className="font-serif text-2xl text-cream-100 mb-6 flex items-center gap-3">
              <Award size={22} strokeWidth={1.2} className="text-gold" />
              {t("proof.panel_title")}
            </h3>
            <div className="grid sm:grid-cols-2 gap-px bg-olive-800 border border-olive-800">
              {panel.map((p, i) => (
                <div
                  key={p.k}
                  data-testid={`proof-panel-${i}`}
                  className="bg-olive-900 p-6"
                >
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] uppercase tracking-[0.26em] text-cream-100/70">
                      {p.k}
                    </span>
                    <span className="font-serif text-xl text-gold">{p.v}</span>
                  </div>
                  <p className="mt-3 text-cream-100/65 text-sm leading-relaxed font-light">
                    {p.note}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-cream-100/45 italic flex items-center gap-2">
              <Thermometer size={12} strokeWidth={1.2} />
              {t("proof.panel_disclaimer")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
